import type {OptionConfig} from '@github-ui/issue-viewer/OptionConfig'
import {RelayCachePreheatContextProvider} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {SidePanel} from '@github-ui/side-panel'
import {testIdProps} from '@github-ui/test-id-props'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useRef} from 'react'

import {
  FocusTrapManagementContextProvider,
  useFocusTrapManagementContext,
} from '../../contexts/FocusTrapManagementProvider'
import {useSiteHeaderHeight} from '../../hooks/use-site-header-height'
import {StaleCacheBanner} from '../StaleCacheBanner'
import styles from './IssueSidePanel.module.css'

type IssueSidePanelProps = {
  onClose: () => void
  children: React.ReactNode
  optionConfig?: Partial<OptionConfig>
}

const IssueSidePanelInternal = ({onClose, children, optionConfig}: IssueSidePanelProps) => {
  const sidePanelReturnFocusRef = useRef(null)
  const sidePanelInitialFocusRef = useRef<HTMLDivElement>(null)
  const siteHeaderSize = useSiteHeaderHeight()
  const {disableFocusTrap} = useFocusTrapManagementContext()

  return (
    <SidePanel
      open
      defaultCloseElement="overlay"
      onClose={onClose}
      initialFocusRef={sidePanelInitialFocusRef}
      returnFocusRef={sidePanelReturnFocusRef}
      aria-labelledby="issue-side-panel"
      width="min(90%, 1280px)"
      topOffset={siteHeaderSize}
      externalFocusTrapDisabled={disableFocusTrap}
    >
      <GlobalCommands
        commands={{
          'issues-react:focus-next-issue': () => optionConfig?.onNavigateToNextItem?.(),
          'issues-react:focus-previous-issue': () => optionConfig?.onNavigateToPreviousItem?.(),
        }}
      />
      <div id="issue-viewer-side-panel" className={styles.IssueSidePanelContent}>
        <span tabIndex={-1} ref={sidePanelInitialFocusRef} {...testIdProps('side-panel-focus-target')} />
        <StaleCacheBanner />
        {children}
      </div>
    </SidePanel>
  )
}

export const IssueSidePanel = ({onClose, children, optionConfig}: IssueSidePanelProps) => {
  return (
    <FocusTrapManagementContextProvider>
      <RelayCachePreheatContextProvider>
        <IssueSidePanelInternal onClose={onClose} optionConfig={optionConfig}>
          {children}
        </IssueSidePanelInternal>
      </RelayCachePreheatContextProvider>
    </FocusTrapManagementContextProvider>
  )
}
